import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  TextField,
} from "@mui/material";
import React, { useContext, useState } from "react";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";
import useFetch from "src/Components/Common/useFetch";
import { userContext } from "src/Components/Contexts/userContext";
import { UserRole } from "src/entity/models/FrontendActor";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { uiLoggerNamesInvoiceRejection } from "src/Utils/Recon/UiLogger/Constants";
import uiLogger from "src/Utils/UiLogger";
import { RejectedInvoicesPriority } from "./RejectedInvoices";

const EditDetailsOfRejectedInvoices = ({
  allUsers,
  openEditDetailsOfRejectedInvoices,
  setOpenEditDetailsOfRejectedInvoices,
  selectedRowForEdit,
  role,
  listOpenInvoices,
  listCloseInvoices,
}) => {
  const { actor } = useContext(userContext);
  const [newMaker, setNewMaker] = useState(selectedRowForEdit.maker || []);

  const [newChecker, setNewChecker] = useState(selectedRowForEdit.checker || []);
  const [newApprover, setNewApprover] = useState(selectedRowForEdit.approver || []);
  const [newPriority, setNewPriority] = useState(selectedRowForEdit.priority || null);
  const [isEditing, setIsEditing] = useState(false);

  const handleClose = () => {
    setOpenEditDetailsOfRejectedInvoices(false);
    setNewMaker(null);
    setNewChecker(null);
    setNewApprover(null);
    setNewPriority(null);
  };

  const updateIssueDetails = () => {
    setIsEditing(true);

    useFetch(API_ENDPOINTS.BULK_UPDATE_INVOICES(role).url, "POST", {
      failureMessage: API_ENDPOINTS.BULK_UPDATE_INVOICES(role).failureMessage,

      data: {
        makers: newMaker.map((maker) => maker.id), // Extracts only the IDs from currentMaker
        checkers: role === UserRole.Maker ? null : newChecker.map((checker) => checker.id), // Extracts only the IDs from currentChecker
        approvers: role === UserRole.DRApprover ? newApprover.map((approver) => approver.id) : null, // Extracts only the IDs from currentApprover
        priority: newPriority,
        grnIds: [selectedRowForEdit?.grnNumber], // Extract only grnNumber from selectedRow
        comment: null,
        markAllCommentAsRead: null,
      },
      showSuccessToast: true,
      thenCallBack: () => {
        listOpenInvoices();
        listCloseInvoices();

        setIsEditing(false);
        handleClose();
      },
      catchCallBack: () => {
        setIsEditing(false);
      },
    });

    uiLogger(uiLoggerNamesInvoiceRejection.UI_SST_IR_EDITDETAILS_UPDATE_CLICK.functionName, null, null, {
      message: uiLoggerNamesInvoiceRejection.UI_SST_IR_EDITDETAILS_UPDATE_CLICK.message,
    });
  };
  return (
    <div>
      <Dialog open={openEditDetailsOfRejectedInvoices} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m">Edit Details</div>
          <div className="vertical_center_align">
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, padding: 2 }}>
            <Autocomplete
              multiple
              size="small"
              className="update-custom-border-radius"
              onChange={(_, newValue) => {
                setNewMaker(newValue);
              }}
              value={newMaker}
              disableClearable
              options={allUsers} // Show all users
              renderOption={(props, item) => (
                <li {...props} key={item.id}>
                  <ListItemText>{item.name}</ListItemText>
                </li>
              )}
              getOptionLabel={(option: { name: string; id: number }) => option.name}
              renderInput={(params) => <TextField {...params} label="Maker" />}
            />
            <Autocomplete
              multiple
              size="small"
              className="update-custom-border-radius"
              onChange={(_, newValue) => {
                setNewChecker(newValue);
              }}
              value={newChecker}
              disableClearable
              options={allUsers?.filter(
                (user) =>
                  user.role === UserRole.DRChecker ||
                  user.role === UserRole.DRApprover ||
                  actor.userRole === UserRole.ReconDRApprover
              )} // Filter users by role
              renderOption={(props, item) => (
                <li {...props} key={item.id}>
                  <ListItemText>{item.name}</ListItemText>
                </li>
              )}
              getOptionLabel={(option: { name: string; id: number }) => option.name}
              renderInput={(params) => <TextField {...params} label="Checker" />}
            />
            {(actor.userRole === UserRole.DRApprover || actor.userRole === UserRole.ReconDRApprover) && (
              <Autocomplete
                multiple
                size="small"
                className="update-custom-border-radius"
                onChange={(_, newValue) => {
                  setNewApprover(newValue);
                }}
                disableClearable
                value={newApprover}
                options={allUsers?.filter(
                  (user) => user.role === UserRole.DRApprover || actor.userRole === UserRole.ReconDRApprover
                )} // Filter users by role
                renderOption={(props, item) => (
                  <li {...props} key={item.id}>
                    <ListItemText>{item.name}</ListItemText>
                  </li>
                )}
                getOptionLabel={(option: { name: string; id: number }) => option.name}
                renderInput={(params) => <TextField {...params} label="Approver" />}
              />
            )}
            <Autocomplete
              size="small"
              className="update-custom-border-radius"
              onChange={(_, newValue) => {
                setNewPriority(newValue);
              }}
              disableClearable
              value={newPriority}
              options={Object.values(RejectedInvoicesPriority)}
              renderInput={(params) => <TextField {...params} label="Priority" />}
            />
          </Box>
        </DialogContent>

        <DialogActions className="dialog-actions">
          <CustomButton size="medium" colorToken="violet" variant="text" onClick={handleClose}>
            Cancel
          </CustomButton>
          <CustomButton
            size="medium"
            colorToken="violet"
            variant="contained"
            disabled={isEditing}
            startIcon={<LoadingIcon loading={isEditing} />}
            onClick={() => {
              updateIssueDetails();
            }}
          >
            Update
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EditDetailsOfRejectedInvoices;
